import {useEffect} from "react";
import {useLexicalComposerContext} from "@lexical/react/LexicalComposerContext";
import {
    $getSelection,
    $isRangeSelection,
    $copyNode,
    $isElementNode,
    LexicalEditor,
    LexicalNode,
    ElementNode,
} from "lexical";

function cloneNodeWithChildren(node: LexicalNode): LexicalNode {
    if (!$isElementNode(node)) {
        return $copyNode(node);
    }

    const element = node as ElementNode;
    const cloned = $copyNode(element) as ElementNode;
    const children = element.getChildren();

    for (const child of children) {
        const childClone = cloneNodeWithChildren(child);
        cloned.append(childClone);
    }

    return cloned;
}

function registerDuplicateShortcut(editor: LexicalEditor) {
    return editor.registerRootListener((rootElement, prevRootElement) => {
        const handler = (event: KeyboardEvent) => {
            const isMac = /Mac|iPod|iPhone|iPad/.test(window.navigator.platform);
            const isMod = isMac ? event.metaKey : event.ctrlKey;

            if (!isMod || event.key.toLowerCase() !== "d") {
                return;
            }

            event.preventDefault();
            event.stopPropagation();

            editor.update(() => {
                const selection = $getSelection();
                if (!$isRangeSelection(selection)) return;

                const anchorNode = selection.anchor.getNode();

                if (anchorNode.getType() === "root") return;

                let topLevel;
                try {
                    topLevel = anchorNode.getTopLevelElementOrThrow();
                } catch {
                    return;
                }

                const cloned = cloneNodeWithChildren(topLevel);
                if (!cloned) return;

                topLevel.insertAfter(cloned);

                if ($isElementNode(cloned)) {
                    cloned.selectEnd();
                }
            });
        };

        if (prevRootElement) {
            prevRootElement.removeEventListener("keydown", handler);
        }

        if (rootElement) {
            rootElement.addEventListener("keydown", handler);
        }
    });
}

export default function DuplicateLinePlugin() {
    const [editor] = useLexicalComposerContext();

    useEffect(() => {
        const unregister = registerDuplicateShortcut(editor);
        return () => {
            unregister();
        };
    }, [editor]);

    return null;
}


